import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { RegisterCredentials, LoginCredentials, RefreshTokenRequest, ShippingAddress, PasswordResetData } from '../types';
import { clearAuthData } from '../utils/auth';
import { API_BASE_URL, STORAGE_KEYS } from '../constants';

// Create axios instance with default config
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create a separate axios instance for refresh token requests (without auth header)
const refreshApi: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple refresh requests
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (reason?: any) => void;
}> = [];

// Resolve/reject every request that was queued while a token refresh was in
// flight. On success each queued request is handed the new access token so it
// can replay itself; on failure they are all rejected.
const processQueue = (error: any, token: string | null = null): void => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error || !token) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

// Request interceptor to add auth token and the selected UI language
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Append the user's selected language so the backend can resolve localized
    // fields. Defaults to Georgian when the user hasn't chosen a language yet.
    const lang = localStorage.getItem('app_language') || 'ka';
    config.params = { ...(config.params || {}), lang };
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle unauthorized requests and refresh tokens
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    // A 401 from the auth endpoints themselves (wrong email/phone or password,
    // invalid Google token, etc.) is NOT an expired session — it must be passed
    // back to the form so it can show an inline error. Otherwise the refresh +
    // `window.location.href = '/login'` logic below would trigger a full page
    // reload on every failed login attempt.
    const requestUrl: string = originalRequest?.url || '';
    const isAuthEndpoint =
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/register') ||
      requestUrl.includes('/auth/google');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      
      if (!refreshToken || refreshToken === 'undefined' || refreshToken === 'null') {
        clearAuthData();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await refreshApi.post('/auth/token/refresh/', { refresh: refreshToken });
        const { access } = response.data;
        
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access);
        originalRequest.headers.Authorization = `Bearer ${access}`;
        processQueue(null, access);
        
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearAuthData();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data: RegisterCredentials): Promise<AxiosResponse> =>
    api.post('/auth/register', data),

  requestPasswordResetOtp: (identifier: string): Promise<AxiosResponse> =>
    api.post('/auth/password-reset/request-otp', { identifier }),

  resetPassword: (data: PasswordResetData): Promise<AxiosResponse> =>
    api.post('/auth/password-reset/verify', data),

  requestPhoneOtp: (phone: string): Promise<AxiosResponse> =>
    api.post('/auth/phone/request-otp', { phone }),

  verifyPhone: (data: { phone: string; otp_code: string }): Promise<AxiosResponse> =>
    api.post('/auth/phone/verify', data),

  login: (data: LoginCredentials): Promise<AxiosResponse> =>
    api.post('/auth/login', data),

  loginMfaSend: (data: { mfa_token: string; phone?: string }): Promise<AxiosResponse> =>
    api.post('/auth/login/mfa/send', data),

  loginMfaVerify: (data: { mfa_token: string; otp_code: string; phone?: string }): Promise<AxiosResponse> =>
    api.post('/auth/login/mfa/verify', data),
  
  googleLogin: (credential: string): Promise<AxiosResponse> =>
    api.post('/auth/google', { credential }),

  facebookLogin: (accessToken: string): Promise<AxiosResponse> =>
    api.post('/auth/facebook', { access_token: accessToken }),
  
  refreshToken: (data: RefreshTokenRequest): Promise<AxiosResponse> => 
    refreshApi.post('/auth/token/refresh/', data),
  
  getCurrentUser: (): Promise<AxiosResponse> =>
    api.get('/auth/me'),

  updateProfile: (data: { first_name: string; last_name: string; email: string }): Promise<AxiosResponse> =>
    api.put('/auth/me', data),

  changePassword: (data: { current_password: string; new_password: string }): Promise<AxiosResponse> =>
    api.post('/auth/change-password', data),

  updateShippingAddress: (data: ShippingAddress): Promise<AxiosResponse> =>
    api.put('/auth/shipping-address', data),
};

// Products API
export const productsAPI = {
  getProducts: (params?: Record<string, any>): Promise<AxiosResponse> => 
    api.get('/products', { params }),
  
  getProduct: (id: string): Promise<AxiosResponse> => 
    api.get(`/products/${id}`),

  // Related products chosen by shared internal tags (falls back to category).
  getSimilarProducts: (id: string | number, limit: number = 12): Promise<AxiosResponse> =>
    api.get(`/products/${id}/similar`, { params: { limit } }),

  getFeaturedProducts: (limit: number = 4): Promise<AxiosResponse> => 
    api.get('/products/featured/', { params: { limit } }),
  
  getTopRatedProducts: (limit: number = 4): Promise<AxiosResponse> => 
    api.get('/products/top-rated/', { params: { limit } }),
};

// Categories API
export const categoriesAPI = {
  getCategories: (): Promise<AxiosResponse> => 
    api.get('/categories/'),
  
  getCategory: (slug: string): Promise<AxiosResponse> => 
    api.get(`/categories/${slug}/`),
  
  getCategoryProducts: (slug: string): Promise<AxiosResponse> => 
    api.get(`/categories/${slug}/products/`),
  
  getHomepageCategories: (limit: number = 8): Promise<AxiosResponse> => 
    api.get('/categories/homepage/', { params: { limit } }),
};

// Cart API
export const cartAPI = {
  getCart: (): Promise<AxiosResponse> => 
    api.get('/cart/'),
  
  addToCart: (data: { product: number; quantity: number; selected_image?: string; variant_id?: string }): Promise<AxiosResponse> =>
    api.post('/cart/add/', { product_id: data.product, quantity: data.quantity, selected_image: data.selected_image, variant_id: data.variant_id }),
  
  updateCartItem: (data: { item_id: number; quantity: number }): Promise<AxiosResponse> =>
    api.patch(`/cart/update/${data.item_id}/`, { quantity: data.quantity }),
  
  removeFromCart: (itemId: number): Promise<AxiosResponse> =>
    api.delete(`/cart/remove/${itemId}/`),
};

// Wishlist API
export const wishlistAPI = {
  getWishlist: (): Promise<AxiosResponse> =>
    api.get('/wishlist/'),

  getWishlistIds: (): Promise<AxiosResponse> =>
    api.get('/wishlist/ids/'),

  addToWishlist: (productId: number): Promise<AxiosResponse> =>
    api.post(`/wishlist/add/${productId}/`),

  removeFromWishlist: (productId: number): Promise<AxiosResponse> =>
    api.delete(`/wishlist/remove/${productId}/`),
};

// Orders API
export const ordersAPI = {
  checkout: (data: { shipping_address: any }): Promise<AxiosResponse> =>
    api.post('/orders/checkout/', data),

  buyNow: (data: { product_id: number; quantity: number; shipping_address: any; selected_image?: string; variant_id?: string }): Promise<AxiosResponse> =>
    api.post('/orders/buy-now/', data),
  
  getOrders: (): Promise<AxiosResponse> =>
    api.get('/orders/'),
  
  getOrder: (orderId: number): Promise<AxiosResponse> =>
    api.get(`/orders/${orderId}`),
};

// Payments API (Flitt)
export const paymentsAPI = {
  createFlitt: (orderId: number): Promise<AxiosResponse> =>
    api.post('/payments/flitt/create/', { order_id: orderId }),

  getStatus: (orderId: number): Promise<AxiosResponse> =>
    api.get(`/payments/flitt/status/${orderId}`),
};

// Analytics API (visitor + product-view tracking)
export const analyticsAPI = {
  recordVisit: (visitorId: string): Promise<AxiosResponse> =>
    api.post('/analytics/visit', { visitor_id: visitorId }),

  recordProductView: (productId: number, visitorId: string): Promise<AxiosResponse> =>
    api.post(`/analytics/product-view/${productId}`, { visitor_id: visitorId }),
};

// A sale-poster layout template spec (all fields optional so it doubles as a
// partial override patch). Mirrors backend poster_templates.SPEC_DEFAULTS.
export interface PosterTemplateSpec {
  aspect?: string;
  // Which product-card renderer draws this design. Old versions are kept
  // forever, so a design's look never shifts under it; the options come from
  // `card_versions` on the templates response.
  card_version?: number;
  // Which price-stack renderer draws the sale price and struck original.
  price_version?: number;
  // Text-size multipliers over the shipped proportions; 1 renders unchanged.
  eyebrow_scale?: number;
  headline_scale?: number;
  price_scale?: number;
  product_mode?: 'card' | 'cutout' | 'bleed';
  product_scale?: number;
  product_center?: [number, number] | number[];
  text_block?: 'top' | 'bottom';
  text_align?: 'left' | 'center';
  background_kind?: 'brand' | 'solid' | 'asset';
  background_color?: string | null;
  background_asset?: string | null;
  accent_color?: string;
  text_color?: string;
  pill_color?: string;
  pill_text_color?: string;
  show_wordmark?: boolean;
  show_old_price?: boolean;
  default_label?: string;
  eyebrow?: string;
  headline?: string;
  scene_mode?: 'static' | 'ai';
  scene_prompt?: string;
}

// Local pass run over an AI-generated image. 'cutout' replaces the flat backdrop
// the model painted with real transparency; '' leaves the image untouched.
export type AiPostProcess = '' | 'cutout';

// Output shape of a generated image. Sent to the model as a hard constraint —
// without one it copies the aspect ratio of the source photo, so a catalogue of
// mixed-shape product photos yields mixed-shape results.
export type AiAspectRatio =
  '1:1' | '2:3' | '3:2' | '3:4' | '4:3' | '4:5' | '5:4' | '9:16' | '16:9' | '21:9';

// Output resolution tier. 4:3 at 2K is 2400x1792, for example.
export type AiImageSize = '1K' | '2K' | '4K';

// One style to generate, with optional per-run overrides of what the preset
// stores. Omit a field to use the preset's saved value.
export interface AiPresetSelection {
  id: string;
  prompt?: string;
  background_color?: string | null;
  post_process?: AiPostProcess;
  aspect_ratio?: AiAspectRatio;
  image_size?: AiImageSize;
  // Reusable backdrop plate id, or '' to force no plate for this run.
  background_image_id?: string | null;
}

// Room Preview API (customer-facing "See it in your room" AI feature).
// A room photo is uploaded, generation runs in the background, and the client
// polls until it is done, then fetches the result image as an authenticated blob.
export const roomPreviewAPI = {
  getQuota: (): Promise<AxiosResponse> =>
    api.get('/room-preview/quota'),
  create: (productId: number, photo: File, placementNote = ''): Promise<AxiosResponse> => {
    const formData = new FormData();
    formData.append('file', photo);
    formData.append('product_id', String(productId));
    if (placementNote) formData.append('placement_note', placementNote);
    // Trailing slash matches the backend route and avoids a 307 that could drop
    // the multipart body.
    return api.post('/room-preview/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  get: (id: string): Promise<AxiosResponse> =>
    api.get(`/room-preview/${id}`),
  // The result is not publicly served; it needs the bearer token, so it is
  // fetched as a blob and shown via URL.createObjectURL.
  getImage: (id: string): Promise<AxiosResponse> =>
    api.get(`/room-preview/${id}/image`, { responseType: 'blob' }),
};

// Admin API
export const adminAPI = {
  getStats: (params?: { from_date?: string; to_date?: string; most_viewed_limit?: number }): Promise<AxiosResponse> =>
    api.get('/admin/stats', { params }),
  getFinanceStats: (params?: { from_date?: string; to_date?: string }): Promise<AxiosResponse> =>
    api.get('/admin/stats/finance', { params }),

  createExpense: (data: {
    category: string;
    amount: number;
    note?: string;
    fb_ad_id?: string;
    /** First day of the spend period. Defaults to today. */
    date?: string;
    /** Last day of the spend period. Omit for a single-day expense. */
    end_date?: string;
    /** Still running, so there is no end date yet. */
    ongoing?: boolean;
  }): Promise<AxiosResponse> =>
    api.post('/admin/expenses', data),
  deleteExpense: (id: string): Promise<AxiosResponse> =>
    api.delete(`/admin/expenses/${id}`),

  getProducts: (params?: {
    limit?: number;
    offset?: number;
    search?: string;
    category?: string;
    in_stock?: boolean;
    active_only?: boolean;
  }): Promise<AxiosResponse> =>
    api.get('/admin/products', { params }),
  getProduct: (id: number): Promise<AxiosResponse> =>
    api.get(`/admin/products/${id}`),
  searchProducts: (search: string): Promise<AxiosResponse> =>
    api.get('/admin/products', { params: { search, limit: 50 } }),
  createProduct: (data: any): Promise<AxiosResponse> =>
    api.post('/admin/products', data),
  updateProduct: (id: number, data: any): Promise<AxiosResponse> =>
    api.put(`/admin/products/${id}`, data),
  quickUpdateProduct: (id: number, data: { price?: number; purchase_price?: number; stock?: number }): Promise<AxiosResponse> =>
    api.patch(`/admin/products/${id}/quick`, data),
  deleteProduct: (id: number): Promise<AxiosResponse> =>
    api.delete(`/admin/products/${id}`),
  toggleProductActive: (id: number): Promise<AxiosResponse> =>
    api.patch(`/admin/products/${id}/toggle-active`),
  // Enable/disable the customer "See it in your room" AI preview per product.
  toggleProductRoomPreview: (id: number): Promise<AxiosResponse> =>
    api.patch(`/admin/products/${id}/toggle-room-preview`),
  applyBulkRoomPreview: (data: { product_ids: number[]; enabled: boolean }): Promise<AxiosResponse> =>
    api.post('/admin/products/bulk-room-preview', data),
  applyBulkStatus: (data: { product_ids: number[]; is_active: boolean }): Promise<AxiosResponse> =>
    api.post('/admin/products/bulk-status', data),
  // Set one category (and optional subcategory) on every selected product.
  applyBulkCategory: (data: {
    product_ids: number[];
    category_en: string;
    category_ka?: string;
    subcategory_en?: string;
    subcategory_ka?: string;
  }): Promise<AxiosResponse> =>
    api.post('/admin/products/bulk-category', data),
  translateFields: (data: { name: string; description: string; color?: string; material?: string; source_lang?: string; target_lang?: string }): Promise<AxiosResponse> =>
    api.post('/admin/translate', data),
  bulkTranslateProducts: (data: { product_ids: number[]; source_lang?: string; target_lang?: string }): Promise<AxiosResponse> =>
    api.post('/admin/products/bulk-translate', data),
  // Bulk pricing: raise price by markup% then apply discount%, on a category or
  // the whole catalog. Reversible via revertBulkDiscount.
  applyBulkDiscount: (data: { scope: 'all' | 'category'; category?: string; markup_percentage: number; discount_percentage: number }): Promise<AxiosResponse> =>
    api.post('/admin/products/bulk-discount', data),
  revertBulkDiscount: (data: { scope: 'all' | 'category'; category?: string }): Promise<AxiosResponse> =>
    api.post('/admin/products/bulk-discount/revert', data),
  // Raise list prices so each selected product sells at >= min_margin% (on the
  // discounted price vs purchase cost). Never lowers a price; skips bulk-sale products.
  applyBulkMinMargin: (data: {
    product_ids: number[];
    min_margin: number;
    rounding: 'none' | 'whole' | 'ninety';
  }): Promise<AxiosResponse> =>
    api.post('/admin/products/bulk-min-margin', data),
  getSaleSummary: (): Promise<AxiosResponse> =>
    api.get('/admin/products/bulk-discount/summary'),
  // Add one shared tag to every selected product (keeps existing tags).
  applyBulkTags: (data: { product_ids: number[]; tag: string }): Promise<AxiosResponse> =>
    api.post('/admin/products/bulk-tags', data),
  // Merge same-photo products (differing only by size) into one product whose
  // sizes become priced variants. The first id is the base kept as the group.
  applyGroup: (data: { product_ids: number[] }): Promise<AxiosResponse> =>
    api.post('/admin/products/group', data),
  // Reverse a group: restore the base to a single product and release children.
  applyUngroup: (data: { product_id: number }): Promise<AxiosResponse> =>
    api.post('/admin/products/ungroup', data),
  // `source` selects the backend column mapping (e.g. 'orlo' | 'raul').
  // RAUL sheets carry embedded photos and can be >100 MB, so allow a long
  // timeout and report upload progress to the caller.
  importProducts: (
    file: File,
    source: string = 'auto',
    onProgress?: (percent: number) => void,
  ): Promise<AxiosResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('source', source);
    return api.post('/admin/products/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30 * 60 * 1000,
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      },
    });
  },
  uploadImage: (file: File): Promise<AxiosResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadImages: (files: File[]): Promise<AxiosResponse> => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    return api.post('/admin/upload-images', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  assignProductImage: (id: number, url: string, asMain = false): Promise<AxiosResponse> =>
    api.post(`/admin/products/${id}/images`, { url, as_main: asMain }),

  // AI image studio (Gemini). Presets carry the exact editable prompt text so
  // the admin always sees what will be sent to the model.
  getAiImagePresets: (): Promise<AxiosResponse> =>
    api.get('/admin/ai/image-presets'),
  createAiImagePreset: (data: {
    label_en: string;
    label_ka?: string;
    prompt: string;
    background_color?: string | null;
    // '' for none, 'cutout' to remove the background after generating.
    post_process?: AiPostProcess;
    aspect_ratio?: AiAspectRatio;
    image_size?: AiImageSize;
    background_image_id?: string | null;
  }): Promise<AxiosResponse> =>
    api.post('/admin/ai/image-presets', data),
  updateAiImagePreset: (
    presetId: string,
    data: {
      label_en?: string; label_ka?: string; prompt?: string;
      background_color?: string | null; post_process?: AiPostProcess;
      aspect_ratio?: AiAspectRatio; image_size?: AiImageSize;
      // '' clears the plate, a hex id pins one, omitted leaves it unchanged.
      background_image_id?: string | null;
    },
  ): Promise<AxiosResponse> =>
    api.put(`/admin/ai/image-presets/${presetId}`, data),
  deleteAiImagePreset: (presetId: string): Promise<AxiosResponse> =>
    api.delete(`/admin/ai/image-presets/${presetId}`),
  resetAiImagePreset: (presetId: string): Promise<AxiosResponse> =>
    api.post(`/admin/ai/image-presets/${presetId}/reset`),
  // Switch the Gemini model every AI feature uses. The available options come
  // back with the presets, since the choice is a cost/consistency trade-off.
  setAiImageModel: (imageModel: string): Promise<AxiosResponse> =>
    api.put('/admin/ai/model', { image_model: imageModel }),
  // Show the exact request each preset would send to Gemini, with the fully
  // assembled prompt. Free: no model is called.
  previewAiRequest: (data: {
    presets: AiPresetSelection[];
    extra_instructions?: string;
    product_name?: string;
    // Product real-world size + opt-in flag, so the preview shows the exact
    // scale hint the generator would append (scene presets only).
    product_dimensions?: string;
    size_hint?: boolean;
    has_source?: boolean;
  }): Promise<AxiosResponse> =>
    api.post('/admin/ai/preview-request', data),
  // Generate marketing variants from one uploaded image. `presets` may carry a
  // per-run prompt override; leave `prompt` undefined to use the stored one.
  generateAiImages: (data: {
    source_url: string;
    presets: AiPresetSelection[];
    extra_instructions?: string;
    product_name?: string;
    // Product real-world size + opt-in flag; sent to scene presets as a scale hint.
    product_dimensions?: string;
    size_hint?: boolean;
    // Also extract the bare backdrop of each non-cut-out result (one extra call
    // per style) so it can be saved and reused.
    capture_backdrop?: boolean;
  }): Promise<AxiosResponse> =>
    api.post('/admin/ai/generate-images', data),
  // ─── Poster studio: layout templates ───────────────────────
  getPosterTemplates: (): Promise<AxiosResponse> =>
    api.get('/admin/ai/poster-templates'),
  createPosterTemplate: (data: {
    label_en: string; label_ka?: string; spec?: PosterTemplateSpec;
  }): Promise<AxiosResponse> =>
    api.post('/admin/ai/poster-templates', data),
  updatePosterTemplate: (
    templateId: string,
    data: { label_en?: string; label_ka?: string; spec?: PosterTemplateSpec },
  ): Promise<AxiosResponse> =>
    api.put(`/admin/ai/poster-templates/${templateId}`, data),
  deletePosterTemplate: (templateId: string): Promise<AxiosResponse> =>
    api.delete(`/admin/ai/poster-templates/${templateId}`),
  resetPosterTemplate: (templateId: string): Promise<AxiosResponse> =>
    api.post(`/admin/ai/poster-templates/${templateId}/reset`),

  // ─── Poster studio: reusable background library ─────────────
  getPosterBackgrounds: (): Promise<AxiosResponse> =>
    api.get('/admin/ai/poster-backgrounds'),
  generatePosterBackground: (data: {
    prompt: string; label?: string; aspect?: AiAspectRatio; image_size?: AiImageSize;
  }): Promise<AxiosResponse> =>
    api.post('/admin/ai/poster-backgrounds/generate', data),
  uploadPosterBackground: (file: File, label = ''): Promise<AxiosResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/admin/ai/poster-backgrounds/upload?label=${encodeURIComponent(label)}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deletePosterBackground: (backgroundId: string): Promise<AxiosResponse> =>
    api.delete(`/admin/ai/poster-backgrounds/${backgroundId}`),
  // Promote a captured backdrop (an ai- URL) into the reusable library so it
  // survives cleanup and can be pinned to a preset.
  saveCapturedBackdrop: (data: { source_url: string; label?: string }): Promise<AxiosResponse> =>
    api.post('/admin/ai/poster-backgrounds/from-capture', data),

  // Render a branded sale poster server-side (no AI). The PNG is returned in the
  // response body as a blob and is never stored on the server — the admin
  // downloads it directly.
  generateSalePoster: (data: {
    source_url: string;
    // Optional: omit/0 for a plain product/promo poster (no price / no sale badge).
    price?: number;
    discount_percentage?: number;
    label?: string;
    product_name?: string;
    template_id?: string | null;
    overrides?: PosterTemplateSpec;
    background_id?: string | null;
    cutout_tolerance?: number;
    cutout_feather?: number;
    preview?: boolean;
    eyebrow?: string | null;
    headline?: string | null;
  }): Promise<AxiosResponse<Blob>> =>
    api.post('/admin/ai/sale-poster', data, { responseType: 'blob' }),
  // Render every template as a base64 thumbnail for one sample product (JSON).
  generateSalePosterPreviews: (data: {
    product_id?: number | null;
    source_url?: string | null;
    price?: number | null;
    discount_percentage?: number | null;
    label?: string;
    product_name?: string;
    background_id?: string | null;
    cutout_tolerance?: number;
    cutout_feather?: number;
    template_ids?: string[];
    full?: boolean;
    eyebrow?: string | null;
    headline?: string | null;
  }, config?: { signal?: AbortSignal }): Promise<AxiosResponse> =>
    api.post('/admin/ai/sale-poster-previews', data, config),
  // Render sale posters for the selected products (discounted or not). Returns
  // JSON with a short-lived download_url — the ZIP is not streamed through axios
  // (large batches were failing in Chrome with net::ERR_FAILED 200 OK).
  generateSalePosterBatch: (data: {
    product_ids: number[];
    label?: string;
    template_ids?: string[];
    background_id?: string | null;
    cutout_tolerance?: number;
    cutout_feather?: number;
    eyebrow?: string | null;
    headline?: string | null;
  }): Promise<AxiosResponse<{
    download_url: string;
    filename: string;
    poster_count: number;
    skipped_count: number;
    bytes: number;
  }>> =>
    api.post('/admin/ai/sale-poster-batch', data, {
      // Rendering can take several minutes; the response itself is tiny JSON.
      timeout: 15 * 60 * 1000,
    }),
  // Same as generateSalePosterBatch, but driven by library images + typed prices.
  // Price and discount are optional per item (blank => plain poster).
  generateSalePosterImageBatch: (data: {
    items: { filename: string; price?: number; discount_percentage?: number }[];
    label?: string;
    template_ids?: string[];
    background_id?: string | null;
    cutout_tolerance?: number;
    cutout_feather?: number;
    eyebrow?: string | null;
    headline?: string | null;
  }): Promise<AxiosResponse<{
    download_url: string;
    filename: string;
    poster_count: number;
    skipped_count: number;
    bytes: number;
  }>> =>
    api.post('/admin/ai/sale-poster-image-batch', data, {
      timeout: 15 * 60 * 1000,
    }),
  cleanupUnusedAiImages: (): Promise<AxiosResponse> =>
    api.post('/admin/ai/cleanup-unused'),
  // Discard specific just-generated variants that weren't attached to a product.
  discardAiImages: (urls: string[]): Promise<AxiosResponse> =>
    api.post('/admin/ai/discard', { urls }),
  // Batch AI generation across many products (background job).
  startAiBatch: (data: {
    product_ids: number[];
    presets: AiPresetSelection[];
    extra_instructions?: string;
    // Feed each product's own dimensions to scene presets as a scale hint.
    size_hint?: boolean;
  }): Promise<AxiosResponse> => api.post('/admin/ai/batch', data),
  listAiBatches: (): Promise<AxiosResponse> => api.get('/admin/ai/batch'),
  getAiBatch: (jobId: string): Promise<AxiosResponse> =>
    api.get(`/admin/ai/batch/${jobId}`),
  resumeAiBatch: (jobId: string): Promise<AxiosResponse> =>
    api.post(`/admin/ai/batch/${jobId}/resume`),
  cancelAiBatch: (jobId: string): Promise<AxiosResponse> =>
    api.post(`/admin/ai/batch/${jobId}/cancel`),
  applyAiBatch: (
    jobId: string,
    decisions: { product_id: number; keep_urls: string[]; main_url: string | null }[],
  ): Promise<AxiosResponse> =>
    api.post(`/admin/ai/batch/${jobId}/apply`, { decisions }),

  getCategories: (): Promise<AxiosResponse> =>
    api.get('/admin/categories'),
  createCategory: (data: any): Promise<AxiosResponse> =>
    api.post('/admin/categories', data),
  updateCategory: (id: number, data: any): Promise<AxiosResponse> =>
    api.put(`/admin/categories/${id}`, data),
  deleteCategory: (id: number): Promise<AxiosResponse> =>
    api.delete(`/admin/categories/${id}`),

  getUsers: (): Promise<AxiosResponse> =>
    api.get('/admin/users'),
  // Edit another account's identity. An empty email or phone clears that field;
  // the account must keep at least one of the two.
  updateUser: (
    id: number,
    data: { first_name: string; last_name: string; email: string; phone: string },
  ): Promise<AxiosResponse> =>
    api.patch(`/admin/users/${id}`, data),
  toggleAdmin: (id: number): Promise<AxiosResponse> =>
    api.patch(`/admin/users/${id}/toggle-admin`),
  // One-time superadmin bootstrap: status tells us whether the claim banner
  // should show; claim promotes the current admin after a password confirm.
  getSuperadminStatus: (): Promise<AxiosResponse> =>
    api.get('/admin/superadmin/status'),
  claimSuperadmin: (password: string): Promise<AxiosResponse> =>
    api.post('/admin/superadmin/claim', { password }),
  // Paid-order SMS alerts for one system user. Omit a field to leave it as is;
  // pass an empty phone to fall back to the account number.
  updateUserSmsNotifications: (
    id: number,
    data: { enabled?: boolean; phone?: string },
  ): Promise<AxiosResponse> =>
    api.patch(`/admin/users/${id}/sms-notifications`, data),
  deleteUser: (id: number): Promise<AxiosResponse> =>
    api.delete(`/admin/users/${id}`),

  getOrders: (params?: Record<string, any>): Promise<AxiosResponse> =>
    api.get('/admin/orders', { params }),
  createOrder: (data: {
    items: Array<{ product_id: number; quantity: number; price?: number }>;
    shipping_address: {
      first_name: string;
      last_name: string;
      email: string;
      address: string;
      city: string;
      phone: string;
      zone?: string;
    };
    order_source: string;
    fb_ad_id?: string;
    payment_status?: string;
    status?: string;
    payment_method?: string;
    /** Opt in to the "order received" text; manual orders never send it otherwise. */
    send_sms?: boolean;
    delivery_fee?: number;
    note?: string;
    /** Business date YYYY-MM-DD. Omitted → now. */
    created_at?: string;
  }): Promise<AxiosResponse> =>
    api.post('/admin/orders', data),
  updateOrderStatus: (id: number, status: string): Promise<AxiosResponse> =>
    api.patch(`/admin/orders/${id}/status`, { status }),
  /** Mark COD / manual settlement as paid (or pending / failed) after money arrives. */
  updateOrderPaymentStatus: (id: number, payment_status: string): Promise<AxiosResponse> =>
    api.patch(`/admin/orders/${id}/payment-status`, { payment_status }),
  /** Correct an order's business date (YYYY-MM-DD). */
  updateOrderCreatedAt: (id: number, created_at: string): Promise<AxiosResponse> =>
    api.patch(`/admin/orders/${id}/created-at`, { created_at }),
  // Replaces the line items of a manual order. Stock moves by the difference
  // against what the order already reserved.
  updateOrderItems: (
    id: number,
    data: {
      items: Array<{ product_id: number; quantity: number; price?: number }>;
      delivery_fee?: number;
    },
  ): Promise<AxiosResponse> =>
    api.put(`/admin/orders/${id}/items`, data),

  getSettings: (): Promise<AxiosResponse> =>
    api.get('/admin/settings/'),
  updateSettings: (data: AdminSiteSettings): Promise<AxiosResponse> =>
    api.put('/admin/settings/', data),

  // Facebook Page auto-posting (admin only).
  getFacebookPageStatus: (): Promise<AxiosResponse> =>
    api.get('/admin/facebook/page-status'),
  saveFacebookPageConfig: (data: { page_id: string; access_token: string }): Promise<AxiosResponse> =>
    api.put('/admin/facebook/page-config', data),
  postProductsToFacebook: (
    data: { items: { product_id: number; caption: string }[] }
  ): Promise<AxiosResponse> =>
    api.post('/admin/facebook/post-products', data),

  getLegalPages: (): Promise<AxiosResponse> =>
    api.get('/admin/legal/'),
  updateLegalPage: (slug: string, data: LegalPageAdmin): Promise<AxiosResponse> =>
    api.put(`/admin/legal/${slug}`, data),

  // Database backups (returned as binary blobs for download).
  downloadBackupJson: (): Promise<AxiosResponse> =>
    api.get('/admin/backup', { responseType: 'blob' }),
  downloadBackupMongodump: (): Promise<AxiosResponse> =>
    api.get('/admin/backup/mongodump', { responseType: 'blob' }),

  // Uploaded-file manager.
  getFiles: (): Promise<AxiosResponse> =>
    api.get('/admin/files'),
  getFileInfo: (name: string): Promise<AxiosResponse> =>
    api.get(`/admin/files/${encodeURIComponent(name)}/info`),
  // Full-resolution original (admin-only, returned as a binary blob).
  getOriginalFile: (name: string): Promise<AxiosResponse> =>
    api.get(`/admin/files/${encodeURIComponent(name)}/original`, { responseType: 'blob' }),
  deleteFiles: (filenames: string[]): Promise<AxiosResponse> =>
    api.post('/admin/files/delete', { filenames }),
  downloadFilesZip: (filenames: string[]): Promise<AxiosResponse> =>
    api.post('/admin/files/download-zip', { filenames }, { responseType: 'blob' }),
};

// Site Settings (public footer/contact info). The public endpoint returns the
// localized flat shape; the admin endpoint additionally returns the `*_ka`
// Georgian counterparts of the free-text fields.
export interface SiteSettings {
  phone: string;
  phone_hours: string;
  email: string;
  email_note: string;
  address_line1: string;
  address_line2: string;
  address_country: string;
  facebook_url: string;
  instagram_url: string;
  linkedin_url: string;
  // Per-order delivery charge shown to the customer at cart/checkout.
  delivery_fee: number;
  // Zone-based delivery fees (Tbilisi vs region) used to price delivery.
  delivery_fee_tbilisi: number;
  delivery_fee_region: number;
}

export interface AdminSiteSettings extends SiteSettings {
  phone_hours_ka: string;
  email_note_ka: string;
  address_line1_ka: string;
  address_line2_ka: string;
  address_country_ka: string;
  // Editable SMS templates ({code}/{order} placeholders).
  sms_prefix: string;
  sms_verify_code: string;
  sms_login_code: string;
  sms_reset_code: string;
  sms_order_received: string;
  sms_admin_order_paid: string;
  // Card-gateway commission (percent) charged on paid card orders; subtracted
  // from profit in the finance views.
  payment_commission_percent: number;
  // Small-business income tax (percent of turnover) charged on website orders;
  // subtracted from profit in the finance views.
  income_tax_percent: number;
  // Image processing: WebP quality (40-100) and max longest-side in pixels
  // applied to uploaded and AI-generated product photos.
  image_quality: number;
  image_max_dimension: number;
  // Per-user daily quota for the "See it in your room" AI preview. Each preview
  // is a billable AI call.
  room_preview_daily_limit: number;
}

// Editable legal documents (Terms / Privacy / Cookie policy). Stored as
// markdown text per language and managed from the admin "Legal pages" screen.
export interface LegalPageAdmin {
  slug: string;
  title_en: string;
  title_ka: string;
  body_en: string;
  body_ka: string;
}

// Public shape: the title + markdown body already resolved for one language.
export interface LegalPagePublic {
  slug: string;
  title: string;
  body: string;
}

export const settingsAPI = {
  getSettings: (): Promise<AxiosResponse> =>
    api.get('/settings/'),
  getLegalPage: (slug: string, lang: string): Promise<AxiosResponse> =>
    api.get(`/legal/${slug}`, { params: { lang } }),
};

// A data deletion request raised through Meta's callback. Looked up by the
// confirmation code Meta shows the user.
export interface DeletionRequestStatus {
  confirmation_code: string;
  status: 'pending' | 'completed' | 'no_data' | 'manual_review' | 'failed';
  created_at: string | null;
  completed_at: string | null;
}

export const dataDeletionAPI = {
  getRequestStatus: (confirmationCode: string): Promise<AxiosResponse<DeletionRequestStatus>> =>
    api.get(`/data-deletion/requests/${encodeURIComponent(confirmationCode)}`),
};

export default api;